// Jenn Rock — site content.
//
// The words, links, dates and lists live in src/content/*.json, which Jenn
// (or LightStorm) edits at /admin (the site editor in src/admin, saving
// straight into this repo; Vercel redeploys on every publish). This file
// reads them and exports the same names the components have always used,
// alongside the things that are code, not content (navigation, routes,
// audio files, timings).
//
// Originally mapped from her LightStorm Artist Discovery Form
// submission (8/25/2026). SKELETON PASS — copy is drawn directly from her own
// words, trimmed/edited for site use; anything drafted (not her words) or
// still missing is flagged inline as PLACEHOLDER / FLAG. Mapped per
// MAPPING_KEY.md (v2) at the repo root — the standing LightStorm intake-form
// -> codebase reference. See ASSET_MANIFEST.md for which real file went where.

import home from "@/content/home.json";
import album from "@/content/album.json";
import music from "@/content/music.json";
import epk from "@/content/epk.json";
import tour from "@/content/tour.json";
import links from "@/content/links.json";
import story from "@/content/story.json";
import brand from "@/content/brand.json";

// An optional field left empty in the editor means "not set".
const orNull = (v) => v || null;

// Section 1 — Basic Information
export const ARTIST_NAME = "Jenn Rock";
export const SITE_LOCATION = links.location;

// Top-level site navigation — multi-page routes (template pattern).
// /tour and /shop exist as pages but are deliberately NOT in the nav yet:
// Jenn gave no upcoming tour dates and doesn't sell merch yet, and an empty
// page in the main nav works against her #1 goal ("look more credible").
// Each is a one-line add here once there's real content behind it.
export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Album", to: "/new-album" },
  { label: "About", to: "/about" },
  { label: "Music", to: "/music" },
  { label: "Community", to: "/community" },
  { label: "EPK", to: "/epk" },
  { label: "Contact", to: "/contact" },
];

// PLACEHOLDER, drafted — Jenn didn't supply a tagline. Built from her own
// identity words ("conscious, dualistic, spiritual gangsta, divine but also
// dark feminine"). Confirm wording with her.
export const SITE_TAGLINE = home.tagline;
// Her words: "I make conscious spiritual hiphop but im experimental so i
// also will make r&b inspired music, rock, nu metal, etc."
export const SITE_GENRE_TAGS = epk.genreTags;

// Hero bio — PLACEHOLDER, drafted from her answers (her message, her "why",
// her identity words). Not client-supplied copy yet; confirm with Jenn.
export const HERO_BIO = home.heroBio;

// Shorter mobile-only variant of HERO_BIO.
export const HERO_BIO_MOBILE = home.heroBioMobile;

export const BIO = home.bio;

// Swappable "current announcement" bubble on the Hero page.
export const HERO_ANNOUNCEMENT = {
  ...home.announcement,
  to: "/new-album",
  appearDelayMs: 6500,
};

export const SOCIALS = links.socials;
// No X / Bandsintown provided. Tracking params (?utm_source, ?si, _t) stripped
// from the links she pasted.

// Laylo — she set it up after the first draft (email, 10/4).
export const LAYLO_URL = links.textList.url;

// List incentive (MAPPING_KEY Section 7 -> leadmagnet_description), her words:
// "Inspirational texts, new song & merch alert. Free prizes discounts."
export const LEADMAGNET_DESCRIPTION = links.textList.leadMagnet;

// No GoFundMe / crowdfunding link provided.
export const GOFUNDME_URL = null;

// Podcast — she's launching one ("Will give you the podcast info once I
// launch it"). Nothing renders for it until this is set.
export const PODCAST_URL = orNull(music.podcastUrl);

// Management / booking — her form's "Primary Booking Contact" answer:
// "Dwaine Harris jennrockmanagement@gmail.com". Her personal address
// (the one she emails us from) is deliberately NOT published anywhere.
export const MANAGEMENT_NAME = links.managementName;
export const SITE_CONTACT = {
  email: links.contact.email,
  phone: orNull(links.contact.phone),
};
export const BOOKING_EMAIL = links.bookingEmail;
// The EPK booking form posts here (Formspree emails each inquiry and keeps
// them all in its dashboard). The Formspree account is Jenn's own.
export const BOOKING_FORM_ENDPOINT = "https://formspree.io/f/xrpppyqe";

// EPK — her numbers are approximate, per her own answers ("Off the top of my
// head like 50", "40k on IG", 3,000–4,000 monthly streams — shown as 3.5K per
// Trevor). The Radiance 2019 headline slot still leads Selected Performances.
export const EPK_STATS = epk.stats;

// Listed under "Notable collaborations" — features/production on her album,
// not support-act billing. Rendered under a "Collaborations" label (not
// Sierra's "Direct Support").
export const EPK_COLLABORATIONS = epk.collaborations;
export const EPK_BAND_NAME = null; // Performs solo — no band name given.
export const EPK_BANNER_TEXT = epk.bannerText;

// Her full performance history, from her email (10/4), newest first. Copied
// as she gave it; venue/city names are hers.
export const PERFORMANCES = epk.performances;

// Short names for the EPK's slow ticker (venues/events she's played).
export const FESTIVALS = epk.festivals;

// Live performance videos she linked alongside the list (YouTube IDs).
export const LIVE_VIDEO_YOUTUBE_IDS = epk.liveVideos.map((v) => v.id).filter(Boolean);

export const BOOKING_STATUS = epk.bookingStatus;
export const BOOKING_COPY = epk.bookingCopy;

// No current tour dates provided (blank in the form). /tour renders a
// "dates coming soon" state while this is empty, and stays out of the nav.
export const TOUR_DATES = tour.dates;
export const TOUR_TICKETS_URL = orNull(tour.ticketsUrl);
export const TOUR_INTRO_LINE_1 = tour.introLine1;
export const TOUR_INTRO_LINE_2 = tour.introLine2;

// Hero copy for the pinned Portal -> Hero experience
export const HERO_TAGLINE = SITE_TAGLINE;
export const HERO_CTA = { label: home.heroButtonLabel, to: "/about" };

// No merch store yet — she doesn't currently sell merch but wants to ("future
// merch", "increase ... sales in merch"). /shop shows a "coming soon" page.
export const WIX_SHOP_URL = orNull(links.shopUrl);

// Spotify artist embed — real URL from the form (tracking params stripped).
export const SPOTIFY_ARTIST_URL = music.spotifyArtistUrl;

// Her words, lightly trimmed — from "anything else you'd like us to know".
export const MUSIC_SUBTITLE = music.subtitle;

export const MUSIC_PLATFORM_LINKS = music.platformLinks;

// Released music video (form: "Released Music Videos"). FLAG — the video's
// title couldn't be looked up from this build environment (YouTube blocked);
// fill in MUSIC_VIDEO_TITLE once confirmed.
export const MUSIC_VIDEO_YOUTUBE_ID = music.musicVideo.id;
export const MUSIC_VIDEO_TITLE = orNull(music.musicVideo.title);

// The album — "The Enlightenment". Her answer to "one song to understand
// you" is the title single; her proudest accomplishment is the album itself
// (Nitty Scott + AKTHESAVIOR features, RJ Pasin producing/engineering).
// Album art is the "Jenn Rock Cover Art Chakras" upload, which carries the
// album title on it.
export const ALBUM = {
  title: album.title,
  subtitle: album.subtitle,
  single: album.single,
  // Her words on why the title single is the one song to know her by.
  singleNote: album.singleNote,
};

// FLAG — no album-specific streaming URLs were given, only artist-level
// profiles. These point at her artist pages until album deep links exist.
export const STREAMING_LINKS = album.streamingLinks;
export const DIRECT_PURCHASE_URL = orNull(album.directPurchaseUrl);

// Homepage feature song, per her form: "Goddess in the flesh- on
// soundcloud. Timestamp: 1.49" — also her "ONE song that best represents
// your artistic world". FLAG (rights): she also wrote that music on
// SoundCloud may use beats she doesn't own the rights to. Confirm this
// track's beat is cleared before using it as site audio (the Portal/Hero
// hook clip, trimmed from 1:49). No audio file is wired in yet.
export const FEATURED_SONG = {
  title: "Goddess In The Flesh",
  hookTimestamp: "1:49",
  // 45s from 1:49 (her pick in the form), loudness-normalized, 1s fade in /
  // 4s fade out. Cut from the file she emailed 10/4.
  audioSrc: "/jennrock-goddess-in-the-flesh-clip.mp3",
  // Same clip at half volume (-6 dB) for the mobile tap, so it isn't
  // abrasive. Baked into the file because iOS ignores audio.volume.
  audioSrcMobile: "/jennrock-goddess-in-the-flesh-clip-mobile.mp3",
};

// Tracking pixel — no IDs provided, same placeholder pattern as template.
export const TRACKING_PIXEL_IDS = {
  metaPixelId: null,
  googleAnalyticsId: null,
};

// Her logo: the "JENNROCK" logotype and her eye/crown/crystals symbol.
export const LOGO = { wordmark: brand.wordmark, symbol: brand.symbol };

// Album artwork for the CD case on the album page.
export const ALBUM_ART = { cover: album.cover, disc: album.disc };

// Press-kit photo gallery, in the order it's shown.
export const EPK_GALLERY = epk.photos;

// The photo behind the Community (join) page.
export const COMMUNITY_PHOTO = links.communityPhoto;

// Her story (About page): one chapter per screen. Each kind maps to the
// flags the About page has always used; empty optional text is left out.
const orUndefined = (v) => (v ? v : undefined);
export const ABOUT_STAGES = story.chapters.map((c) => {
  if (c.type === "invitation") return { id: c.id, cta: true };
  if (c.type === "video") return { id: c.id, video: true, showChevron: true };
  if (c.type === "bridge") return { id: c.id, bridge: true, kicker: orUndefined(c.kicker), body: c.body[0] || "", showChevron: true };
  return { id: c.id, kicker: orUndefined(c.kicker), title: orUndefined(c.title), media: c.image, body: c.body, showChevron: true };
});

// The extra photos under her story ("A Few More Moments").
export const ABOUT_MORE_PHOTOS = story.morePhotos;
